import { useMemo } from 'react';
import { Card, Stack, useTheme } from '@mui/material';

import { Strings } from '@app/constants';
import { LeavesTakenCard, TotalLeavesChart } from '@app/components/card';
import { IManagerDashboard } from '../type';
import { ILeave } from '@app/modules/add-leave-employee';
import { ILeaveManagement } from '@app/modules/leave-setting';
import { IPeriods } from '@app/modules/employees';

interface Props {
  data?: IManagerDashboard;
}

interface LeavePeriod {
  period: string;
}

interface CurrentLeave {
  leaveType: string;
  update: string;
  periods: LeavePeriod[];
}

const MangerLeavesPage = ({ data }: Props) => {
  const theme = useTheme();

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const leavesByMonth = useMemo(() => {
    const initialLeavesByMonth = months?.map((month) => ({
      month,
      leavesTaken: 0,
      extraLeaves: 0,
    }));
    const leaveCountPerMonth: Record<number, number> = {};

    data?.leaves?.forEach((leave: ILeave) => {
      leave.periods?.forEach((period: IPeriods) => {
        const [day, month, year] = period?.date?.split('/') ?? [];
        if (!day || !month || !year) return;

        const dateObj = new Date(`${year}-${month}-${day}`);
        const monthIndex = dateObj.getMonth();
        const dayValue = period.period === 'Full Day' ? 1 : 0.5;

        if (!leaveCountPerMonth[monthIndex]) {
          leaveCountPerMonth[monthIndex] = 0;
        }

        const monthData = initialLeavesByMonth[monthIndex];
        const maxAllowed = 1.5;
        const alreadyTaken = leaveCountPerMonth[monthIndex];
        const availableSlot = Math.max(0, maxAllowed - alreadyTaken);
        const toAssign = Math.min(dayValue, availableSlot);
        monthData.leavesTaken += toAssign;
        leaveCountPerMonth[monthIndex] += toAssign;

        if (dayValue > toAssign) {
          monthData.extraLeaves += dayValue - toAssign;
        }
      });
    });

    return initialLeavesByMonth;
  }, [data]);

  const approvedLeaves = useMemo(() => {
    return data?.leaveManagemaent?.map((leave: ILeaveManagement) => {
      const leaveType = leave.leaveType;
      const allowedDays = leave.allowedDays;
      const taken = data?.leaves?.reduce((acc: number, currentLeave: CurrentLeave) => {
        if (currentLeave.leaveType === leaveType && currentLeave.update === 'Approved') {
          acc += currentLeave.periods.reduce((periodAcc, period) => {
            return periodAcc + (period.period === 'Full Day' ? 1 : 0.5);
          }, 0);
        }
        return acc;
      }, 0);
      const remaining = Number(allowedDays) - taken;
      return { leaveType, value: allowedDays, taken, remaining };
    });
  }, [data]);

  const valueFormatter = (value: number) => `${value} days`;

  const chartSetting = {
    yAxis: [
      {
        label: 'Days',
      },
    ],
    series: [
      {
        dataKey: 'leavesTaken',
        label: 'Allowed',
        color: theme.palette.quaternary.royalBlue,
        valueFormatter,
        stack: 'A',
      },
      {
        dataKey: 'extraLeaves',
        label: 'Extra',
        color: theme.palette.taskboard.lightRed,
        valueFormatter,
        stack: 'A',
      },
    ],
    height: 300,
    barWidth: 20,
    barGap: 0,
  };

  return (
    <Stack flexDirection={{ sm: 'column', md: 'row' }} mb={3}>
      <Card
        sx={{
          width: { sm: '97%', md: '48%' },
          height: '380px',
          margin: '0 2% 0 2%',
          marginTop: { xs: '2%', sm: '2%', md: '0%' },
          borderRadius: '5px',
        }}
      >
        <TotalLeavesChart
          title={Strings.employeeDashborad.totalLeaves}
          leaveData={approvedLeaves}
          allowedDays={data?.allowedDays}
        />
      </Card>
      <Card
        sx={{
          width: { sm: '97%', md: '48%' },
          height: '380px',
          margin: { xs: '0 2% 0 2%', sm: '0 2% 0 2%', md: '0 2% 0 0%' },
          marginTop: { xs: '2%', sm: '2%', md: '0%' },
          borderRadius: '5px',
          overflowX: 'auto',
        }}
      >
        <LeavesTakenCard
          title={Strings.employeeDashborad.leavesTaken}
          chartSetting={chartSetting}
          dataSet={leavesByMonth}
        />
      </Card>
    </Stack>
  );
};

export { MangerLeavesPage };
